import { useState } from 'react';
import { RefreshCw, ArrowLeftRight, Lock, X } from 'lucide-react';
import { useBillingUsage } from '../../../api/hooks/useBilling';
import { TransferPaymentModal } from '../components/TransferPaymentModal';
import { Tooltip } from '../../../components/feedback/Tooltip';
import type { BillingInvite } from '../../../types';

export function PaymentsTab() {
  const { data: usage, isLoading } = useBillingUsage();
  const [transferSource, setTransferSource] = useState<BillingInvite | null>(null);
  const [lockedInfoId, setLockedInfoId] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const allInvites: BillingInvite[] = usage?.usage?.all_invites ?? [];

  if (allInvites.length === 0) {
    return (
      <div className="max-w-4xl">
        <div className="bg-background-card border border-border rounded-xl p-8 text-center">
          <p className="text-sm text-text-secondary">No invites yet. Payment history will appear here once you create an invite.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="bg-background-card border border-border rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-base font-semibold text-text-primary">Payment History</h3>
          <p className="text-xs text-text-secondary mt-0.5">All invites and their payment status</p>
        </div>

        <div className="divide-y divide-border">
          {allInvites.map((invite) => {
            const isPaid = !!invite.paid_at && !invite.refunded_at;
            const isRefunded = !!invite.paid_at && !!invite.refunded_at;
            const blockedReason = isRefunded
              ? 'This payment has been refunded and cannot be transferred.'
              : invite.payment_type === 'bespoke'
                ? 'Bespoke payments cannot be transferred.'
                : invite.has_submitted_rsvps
                  ? "This payment can't be transferred as the invite has been used (guests have responded)."
                  : invite.has_emails_sent
                    ? "This payment can't be transferred as the invite has been used (emails have been sent)."
                    : null;
            return (
              <div key={invite.id}>
              <div className="flex items-center justify-between px-6 py-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-text-primary truncate">{invite.title}</p>
                  <p className="text-xs text-text-secondary mt-0.5">
                    {isRefunded
                      ? `Refunded · Paid ${new Date(invite.paid_at!).toLocaleDateString()}`
                      : isPaid
                        ? `Paid ${new Date(invite.paid_at!).toLocaleDateString()} · ${invite.payment_type === 'bespoke' ? 'Bespoke' : 'Per Invite'}`
                        : `Created ${new Date(invite.created_at).toLocaleDateString()}`
                    }
                  </p>
                </div>

                <div className="flex items-center gap-3 ml-4 shrink-0">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    isRefunded
                      ? 'bg-error/10 text-error'
                      : isPaid
                        ? invite.status === 'published'
                          ? 'bg-success/10 text-success'
                          : 'bg-primary/10 text-primary'
                        : 'bg-text-secondary/10 text-text-secondary'
                  }`}>
                    {isRefunded ? 'Refunded' : isPaid ? (invite.status === 'published' ? 'Published' : 'Paid · Draft') : 'Unpaid'}
                  </span>

                  {(isPaid || isRefunded) && (() => {
                    return blockedReason ? (
                      <button
                        onClick={() => setLockedInfoId(lockedInfoId === invite.id ? null : invite.id)}
                        className="flex items-center gap-1.5 text-xs text-text-secondary/40 px-2.5 py-1.5 rounded-lg"
                      >
                        <Lock size={13} />
                        Transfer
                      </button>
                    ) : (
                      <button
                        onClick={() => setTransferSource(invite)}
                        className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-primary transition-colors px-2.5 py-1.5 rounded-lg hover:bg-primary/10"
                      >
                        <ArrowLeftRight size={13} />
                        Transfer
                      </button>
                    );
                  })()}
                </div>
              </div>

              {/* Inline locked reason — shown on tap */}
              {lockedInfoId === invite.id && blockedReason && (
                <div className="mx-6 mb-4 flex items-start justify-between gap-3 bg-background rounded-lg border border-border px-4 py-3">
                  <p className="text-xs text-text-secondary">{blockedReason}</p>
                  <button onClick={() => setLockedInfoId(null)} className="text-text-secondary shrink-0 mt-0.5">
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>

      {transferSource && (
        <TransferPaymentModal
          source={transferSource}
          allInvites={allInvites}
          onClose={() => setTransferSource(null)}
        />
      )}
    </div>
  );
}
